import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogIn, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CirculateLogo } from "@/components/CirculateLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { access_token, user } = response.data;
      
      // Store token and user in localStorage
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      
      // Set default auth header for axios
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      toast.success(`Welcome back, ${user.name}!`);
      onLogin(user);
      navigate("/");
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream p-4">
      {/* Rainbow stripe accent */}
      <div className="fixed top-0 left-0 right-0 h-2 brand-gradient" />
      
      <Card className="w-full max-w-md border-none shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <CirculateLogo size={48} showText={false} />
          </div>
          <CardTitle className="font-heading text-3xl" style={{ color: '#5C3D2E' }}>circulate</CardTitle>
          <CardDescription className="text-sm">Library productivity and tracking</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                required
                data-testid="login-email"
                className="border-stone-300 focus:border-[#6B4E8D] focus:ring-[#6B4E8D]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                data-testid="login-password"
                className="border-stone-300 focus:border-[#6B4E8D] focus:ring-[#6B4E8D]"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#6B4E8D] hover:bg-[#5A3D7A]" 
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link to="/register" className="text-primary hover:underline font-medium">
              Request Access
            </Link>
          </div>
          
          {/* Hidden admin setup - click 5 times on the logo area to activate */}
          <button
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/admin/fix-sarah-role`);
                if (res.data.success) {
                  toast.success("Admin privileges activated for Sarah!");
                } else {
                  toast.error(res.data.message);
                }
              } catch (e) {
                toast.error("Setup failed");
              }
            }}
            className="mt-4 text-xs text-muted-foreground/50 hover:text-muted-foreground cursor-pointer"
            data-testid="admin-setup-btn"
          >
            Admin Setup
          </button>
        </CardContent>
      </Card>
    </div>
  );
};
